import React, { useEffect, useMemo, useState } from 'react';
//Componente de mantenedor de usuarios
/* Material */
import { 
  Card, 
  CardContent, 
  Typography, 
  Container, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TextField,
  Grid,
  Chip,
  IconButton,
  Pagination,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemText,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

/* React Redux */
import { useDispatch, useSelector } from 'react-redux';

/* Project */
import { composedComponent, formatRutDisplay } from 'utils/functions';
import Layout from 'commons/containers/Layout/Layout';
import TitleField from 'commons/components/TitleField/TitleField';
import { reselectObjects } from 'utils/helper';
import color from 'utils/colors';

import saga from './UserManagement.saga';
import * as actions from './UserManagement.actions';
import UserDetailModal from './components/UserDetailModal';
import ResetPasswordDialog from './components/ResetPasswordDialog';
import SetPasswordDialog from './components/SetPasswordDialog';
import EditUserModal from './components/EditUserModal';
import RepresentativesModal from './components/RepresentativesModal';
import PowersModal from './components/PowersModal';
import EditNaturalEntity from 'commons/components/EditNaturalEntity/EditNaturalEntity';
import EditLegalEntity from 'commons/components/EditLegalEntity/EditLegalEntity';

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  tableContainer: {
    marginTop: '20px',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  actionButton: {
    marginRight: '8px',
  },
  tableHeader: {
    backgroundColor: color.purpleSecondary,
    '& .MuiTableCell-head': {
      color: color.white,
      fontWeight: 'bold',
    },
  },
}));


function UserManagement(props) {
  
  const classes = useStyles();
  const dispatch = useDispatch();
  const { actions: userActions } = props;
  
  const { userManagement: userManagementState, app: appState } = useSelector((state) => state);
  const { objects: objectsState, user: currentUser } = appState;

  const objects = useMemo(() => reselectObjects(objectsState), [objectsState]);
  
  // Lógica para determinar qué modal de edición mostrar (igual que PublicLayout)
  const { entities, legalEntities } = objects;
  const entity = currentUser ? Object.values(entities).filter((e) => e.userId === currentUser.id) : [];
  const legalEntity = entity.length
    ? Object.values(legalEntities).filter((le) => le.entityId === entity[0].id)
    : [];
  const { 
    loading, 
    loadingUpdate, 
    loadingRepresentatives, 
    loadingPowers, 
    loadingResetPassword, 
    loadingSetPassword,
    loadingPowerOperations,
    users, // is object
    userOrder,
    error, 
    pagination, 
    filters, 
    representatives 
  } = userManagementState;
  // Local state
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm || '');
  const [searchName, setSearchName] = useState('');
  const [searchRut, setSearchRut] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [setPasswordUser, setSetPasswordUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [representativesUser, setRepresentativesUser] = useState(null);
  const [powersUser, setPowersUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [representativesOpen, setRepresentativesOpen] = useState(false);
  const [powersOpen, setPowersOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);

  // Fetch users on component mount and when pagination changes (NOT search changes)
  useEffect(() => {
    const fetchParams = {
      page: pagination.page,
      limit: 10,
      searchName: searchName.trim(),
      searchRut: searchRut.trim(),
      searchEmail: searchEmail.trim(),
      userType: userTypeFilter === 'all' ? '' : userTypeFilter,
      status: filters.status
    };
    
    userActions.fetchUsers(fetchParams);
  }, [pagination.page, userActions]); // Removed search fields and userTypeFilter from dependencies


  const handlePageChange = (event, newPage) => {
    const newPagination = { ...pagination, page: newPage };
    userActions.setPagination(newPagination);
  };


  const handleResetPassword = (user) => {
    userActions.resetPassword(user.email); // Now passing email instead of id
    setResetPasswordOpen(false);
  };

  const handleSetPassword = (user, password) => {
    userActions.setPassword(user.email, password);
    setSetPasswordOpen(false);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setUserDetailOpen(true);
    handleCloseMenu();
  };

  const handleOpenMenu = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const handleMenuAction = (action, user) => {
    switch (action) {
      case 'edit':
        setEditUser(user);
        setEditUserOpen(true);
        handleCloseMenu();
        break;
      case 'representatives':
        setRepresentativesUser(user);
        setRepresentativesOpen(true);
        // Obtener representantes específicos de este usuario
        userActions.fetchUserRepresentatives(user.id);
        handleCloseMenu();
        break;
      case 'powers':
        setPowersUser(user);
        setPowersOpen(true);
        // Obtener poderes específicos de este usuario
        userActions.fetchUserPowers(user.id);
        handleCloseMenu();
        break;
      case 'resetPassword':
        setResetPasswordUser(user);
        setResetPasswordOpen(true);
        handleCloseMenu();
        break;
      case 'setPassword':
        setSetPasswordUser(user);
        setSetPasswordOpen(true);
        handleCloseMenu();
        break;
      default:
        handleCloseMenu();
    }
  };

  const handleSearch = () => {
    // Reset to first page when searching
    const newPagination = { ...pagination, page: 0 };
    userActions.setPagination(newPagination);
    
    // Execute the search with current filters
    const fetchParams = {
      page: 0, // Always start from first page
      limit: 10,
      searchName: searchName.trim(),
      searchRut: searchRut.trim(),
      searchEmail: searchEmail.trim(),
      userType: userTypeFilter === 'all' ? '' : userTypeFilter,
      status: filters.status
    };
    
    userActions.fetchUsers(fetchParams);
  };

  const handleClearSearch = () => {
    setSearchName('');
    setSearchRut('');
    setSearchEmail('');
    setUserTypeFilter('all');
    
    // Reset to first page when clearing search
    const newPagination = { ...pagination, page: 0 };
    userActions.setPagination(newPagination);
    
    // Execute search with cleared filters
    const fetchParams = {
      page: 0,
      limit: 10,
      searchName: '',
      searchRut: '',
      searchEmail: '',
      userType: '',
      status: filters.status
    };
    
    userActions.fetchUsers(fetchParams);
  };

  const handleUserTypeFilterChange = (newUserType) => {
    setUserTypeFilter(newUserType);
    
    // Reset to first page when changing filter
    const newPagination = { ...pagination, page: 0 };
    userActions.setPagination(newPagination);
    
    // Execute search with new filter
    const fetchParams = {
      page: 0,
      limit: 10,
      searchName: searchName.trim(),
      searchRut: searchRut.trim(),
      searchEmail: searchEmail.trim(),
      userType: newUserType === 'all' ? '' : newUserType,
      status: filters.status
    };
    
    userActions.fetchUsers(fetchParams);
  };

  // Handle Enter key press in search fields
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEditUserSave = (userData) => {
    userActions.updateUser(userData.id, userData);
    setEditUserOpen(false);
    setEditUser(null);
  };


  if (loading && Object.keys(users).length === 0) {
    return (
      
        <Container maxWidth="lg" className={classes.container}>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </Container>
      
    );
  }

  return (
    
      <Container maxWidth="lg" className={classes.container}>
        <TitleField title="Mantenedor de usuarios" />
        
        <Card className={classes.tableContainer}>
          <CardContent>
            {error && (
              <Typography color="error" variant="body2">
                Error: {error}
              </Typography>
            )}

            {/* Search Card */}
            <Card sx={{ mb: 3, p: 3 }} elevation={1}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  color: color.darkGrey, 
                  fontWeight: 'bold',
                  height: '24px',
                  lineHeight: '24px',
                  fontSize: '18px'
                }}
              >
                Buscador
              </Typography>
              
              {/* First Row - Search Fields */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Nombre"
                    placeholder="Escribe un nombre"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="RUT"
                    placeholder="Escribe un RUT"
                    value={searchRut}
                    onChange={(e) => setSearchRut(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Correo electrónico"
                    placeholder="Escribe un correo electrónico"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {/* Second Row - Filter and Actions */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel 
                      shrink 
                      sx={{ 
                        color: color.darkGrey,
                        backgroundColor: color.white,
                        paddingX: 1,
                        '&.Mui-focused': { color: color.darkGrey }
                      }}
                    >
                      Filtrar por
                    </InputLabel>
                    <Select
                      value={userTypeFilter}
                      onChange={(e) => handleUserTypeFilterChange(e.target.value)}
                      displayEmpty
                      IconComponent={ArrowDownIcon}
                      sx={{
                        '& .MuiSelect-select': {
                          color: userTypeFilter === 'all' ? color.grey : color.darkGrey,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: color.darkGrey,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: color.darkGrey,
                        },
                        '& .MuiSelect-icon': {
                          color: color.purpleSecondary,
                        },
                      }}
                    >
                      <MenuItem value="all" sx={{ color: color.purpleSecondary }}>
                        Todos los tipos
                      </MenuItem>
                      <MenuItem value="juridica" sx={{ color: color.purpleSecondary }}>
                        Persona Jurídica
                      </MenuItem>
                      <MenuItem value="natural" sx={{ color: color.purpleSecondary }}>
                        Persona Natural
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                    <Button
                      onClick={handleClearSearch}
                      sx={{ color: color.purpleSecondary, textTransform: 'none' }}
                    >
                      Limpiar búsqueda
                    </Button>
                    <Button
                      onClick={handleSearch}
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      sx={{
                        borderColor: color.purpleSecondary,
                        color: color.purpleSecondary,
                        backgroundColor: 'transparent',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: color.purpleSecondary,
                          backgroundColor: `${color.purpleSecondary}10`,
                        }
                      }}
                    >
                      Buscar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Card>


            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>RUT</TableCell>
                    <TableCell>Tipo de persona</TableCell>
                    <TableCell>Correo</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Dirección</TableCell>
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(userOrder || []).map((userId) => {
                    const user = users[userId];
                    return (
                      <TableRow key={user.id} className={classes.tableRow}>
                        <TableCell>
                          <Typography variant="body2">
                            {`${user.names || user.name || ''} ${user.surnames || ''}`.trim() || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap' }}>
                            {formatRutDisplay(user.rut)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={user.userType === 'natural' ? <PersonIcon /> : <BusinessIcon />}
                            label={user.userType === 'natural' ? 'Natural' : 'Jurídica'}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: '500',
                              borderColor: color.grey,
                              color: color.darkGrey,
                              backgroundColor: 'transparent',
                              '& .MuiChip-icon': {
                                color: color.grey,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone || '-'}</TableCell>
                        <TableCell>{user.address || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(event) => handleOpenMenu(event, user.id)}
                            size="small"
                            sx={{ color: color.purpleSecondary }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )})
                  }
                  {Object.keys(users).length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No hay usuarios para mostrar
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Custom Pagination like Home */}
              <Grid container flexDirection="row" justifyContent="center" sx={{ mt: 3, mb: 2 }}>
                <Pagination
                  count={Math.ceil((pagination.total || 0) / 10)}
                  page={(pagination.page || 0) + 1}
                  variant="outlined"
                  shape="rounded"
                  disabled={loading}
                  onChange={(event, value) => handlePageChange(event, value - 1)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: color.purpleSecondary,
                      borderColor: color.purpleSecondary,
                    },
                    '& .MuiPaginationItem-root:hover': {
                      backgroundColor: `${color.purpleSecondary}10`,
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: color.purpleSecondary,
                      color: color.white,
                    },
                    '& .MuiPaginationItem-root.Mui-selected:hover': {
                      backgroundColor: color.purpleSecondary,
                    },
                  }}
                />
              </Grid>
              
              {/* Results counter */}
              <Grid container justifyContent="center" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  {pagination.total > 0 && (
                    <Typography variant="body2" sx={{ color: color.purpleSecondary }}>
                      {`${(pagination.page || 0) * 10 + 1}-${Math.min(
                        ((pagination.page || 0) + 1) * 10,
                        pagination.total || 0
                      )} de ${pagination.total || 0}`}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Editar - Disponible para todos */}
          <MenuItem 
            onClick={() => {
              const user = users[menuUserId] || {};
              if (user) handleMenuAction('edit', user);
            }}
            disabled={loadingUpdate}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {loadingUpdate && (
                <CircularProgress size={16} sx={{ color: color.purpleSecondary }} />
              )}
              <ListItemText sx={{ color: color.purpleSecondary }}>Editar</ListItemText>
            </Box>
          </MenuItem>
          
          {/* Representantes Asociados - Solo para personas jurídicas */}
          {(() => {
            const user = users[menuUserId] || {};
            return user?.userType === 'juridica' && (
              <MenuItem 
                onClick={() => {
                  if (user) handleMenuAction('representatives', user);
                }}
                disabled={loadingRepresentatives}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {loadingRepresentatives && (
                    <CircularProgress size={16} sx={{ color: color.purpleSecondary }} />
                  )}
                  <ListItemText sx={{ color: color.purpleSecondary }}>Representantes Asociados</ListItemText>
                </Box>
              </MenuItem>
            );
          })()}
          
          {/* Poderes Cargados - Solo para personas jurídicas */}
          {(() => {
            const user = users[menuUserId] || {};
            return user?.userType === 'juridica' && (
              <MenuItem 
                onClick={() => {
                  if (user) handleMenuAction('powers', user);
                }}
                disabled={loadingPowers}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {loadingPowers && (
                    <CircularProgress size={16} sx={{ color: color.purpleSecondary }} />
                  )}
                  <ListItemText sx={{ color: color.purpleSecondary }}>Poderes Cargados</ListItemText>
                </Box>
              </MenuItem>
            );
          })()}
          
          {/* Seteo de contraseña - Disponible para todos */}
          <MenuItem 
            onClick={() => {
              const user = users[menuUserId] || {};
              if (user) handleMenuAction('setPassword', user);
            }}
            disabled={loadingSetPassword}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {loadingSetPassword && (
                <CircularProgress size={16} sx={{ color: color.purpleSecondary }} />
              )}
              <ListItemText sx={{ color: color.purpleSecondary }}>Seteo de contraseña</ListItemText>
            </Box>
          </MenuItem>

          {/* Reseteo de contraseña - Disponible para todos */}
          <MenuItem 
            onClick={() => {
              const user = users[menuUserId] || {};
              if (user) handleMenuAction('resetPassword', user);
            }}
            disabled={loadingResetPassword}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {loadingResetPassword && (
                <CircularProgress size={16} sx={{ color: color.purpleSecondary }} />
              )}
              <ListItemText sx={{ color: color.purpleSecondary }}>Reseteo de contraseña</ListItemText>
            </Box>
          </MenuItem>
        </Menu>

        {/* User Detail Modal */}
        <UserDetailModal
          open={userDetailOpen}
          onClose={() => setUserDetailOpen(false)}
          user={selectedUser}
        />

        {/* Reset Password Dialog */}
        <ResetPasswordDialog
          open={resetPasswordOpen}
          onClose={() => setResetPasswordOpen(false)}
          user={resetPasswordUser}
          onConfirm={handleResetPassword}
        />

        {/* Set Password Dialog */}
        <SetPasswordDialog
          open={setPasswordOpen}
          onClose={() => setSetPasswordOpen(false)}
          user={setPasswordUser}
          onConfirm={handleSetPassword}
          loading={loadingSetPassword}
        />

        {/* Edit User Modal */}
        <EditUserModal
          open={editUserOpen}
          onClose={() => {
            setEditUserOpen(false);
            setEditUser(null);
          }}
          user={editUser}
          onSave={handleEditUserSave}
        />

        {/* Representatives Modal */}
        <RepresentativesModal
          open={representativesOpen}
          onClose={() => {
            setRepresentativesOpen(false);
            setRepresentativesUser(null);
          }}
          user={representativesUser}
          representatives={userManagementState.representatives || []}
          loading={loadingRepresentatives}
        />

        {/* Powers Modal */}
        <PowersModal
          open={powersOpen}
          onClose={() => {
            setPowersOpen(false);
            setPowersUser(null);
          }}
          user={powersUser}
          powers={userManagementState.powers || []}
          loading={loadingPowers}
          loadingOperations={loadingPowerOperations}
        />

        {/* Modals de edición de perfil - igual que en PublicLayout */}
        {legalEntity.length ? <EditLegalEntity /> : <EditNaturalEntity />}
      </Container>
    
  );
}

export default composedComponent(UserManagement, saga, {
  saga: 'sagaUserManagement',
  states: [
    'userManagement.loading',
    'userManagement.users',
    'userManagement.error',
  ],
  actions: [actions],
});